// src/axl2/OcxoParams/A1LayoutOcxoParams.jsx
import React, { useState, useEffect } from 'react';
import { useViewport } from '../../context/ViewportContext';
import { useModelData } from '../../hooks/useModelData';
import { getPartNumberCode } from '../../service/serviseApi';

const A1LayoutOcxoParams = () => {
  const { isMobile } = useViewport();
  const {
    formData,
    selectedFormAdditionalRequirements,
    partNumberConfig,
    currentProduct,
    actions
  } = useModelData();

  // Local state for supply and signal selections
  //const [selectedVoltage, setSelectedVoltage] = useState('');
  //const [selectedSignal, setSelectedSignal] = useState('');

  // Extract configuration from partNumberConfig or use defaults
  const temperatureRanges = partNumberConfig?.temperatureRanges || [
/*     { code: "5", range: "0..50ºC" },
    { code: "6", range: "-10..60ºC" },
    { code: "7", range: "-30..70ºC" },
    { code: "8", range: "-40..80ºC" },
    { code: "9", range: "-40..85ºC" },
    { code: "C", range: "Custom" } */
  ];

  // Separate supply and signal types
  const supplyTypes = partNumberConfig?.supplyTypes || [
/*     { voltage: "3.3V", value: "3.3V" },
    { voltage: "5V", value: "5V" } */
  ];

  const signalTypes = partNumberConfig?.signalTypes || [
/*     { code: "HCMOS", value: "HCMOS", label: "HCMOS" },
    { code: "Sine", value: "Sine", label: "Sine Wave" }, */
  ];

  const agingOptions = partNumberConfig?.agingOptions || [
/*     { code: "29", ppbPerDay: "2" },
    { code: "50", ppbPerDay: "0.5" },
    { code: "20", ppbPerDay: "0.2" },
    { code: "10", ppbPerDay: "0.1" },
    { code: "05", ppbPerDay: "0.05" },
    { code: "03", ppbPerDay: "0.03" } */
  ];

  const stabilityOptions = partNumberConfig?.stabilityOptions || [
/*     { code: "50", value: "0.5", unit: "ppb" },
    { code: "19", value: "1", unit: "ppb" },
    { code: "29", value: "2", unit: "ppb" },
    { code: "39", value: "3", unit: "ppb" },
    { code: "59", value: "5", unit: "ppb" },
    { code: "18", value: "10", unit: "ppb" },
    { code: "28", value: "20", unit: "ppb" },
    { code: "38", value: "30", unit: "ppb" },
    { code: "58", value: "50", unit: "ppb" } */
  ];

  // Initialize selections from existing formData if available
  //console.log("formData", formData)
  const handleInputChange = (field, value) => {
    switch (field) {
      case 'tempRange':
        actions.setSelectedTemperatureRanges(value);
        break;
      case 'frequency':
        actions.setSelectedFrequency(value);
        break;
      case 'additionalRequirements':
          //actions.setSelectedFrequency(value);
          //console.log('value: ', value)
          actions.setFormAdditionalRequirements(value);
          break;           
      case 'voltage':
        //setSelectedVoltage(value);
        // Combine voltage and signal to create supplySignal value
        if (formData.signal) {
          const supplySignalValue = generateSupplySignalValue(formData.signal, value);
          actions.setSelectedSupplySignal(supplySignalValue);
          actions.setSelectedSupply(value);
        }
        break;
      case 'signal':
        //setSelectedSignal(value);
        // Combine voltage and signal to create supplySignal value
        if (formData.supply) {
          const supplySignalValue = generateSupplySignalValue(value, formData.voltage);
          actions.setSelectedSupplySignal(supplySignalValue);
          actions.setSelectedSignal(value);
        }
        break;
      case 'aging':
        actions.setSelectedAgingOptions(value);
        break;
      case 'stability':
        actions.setSelectedStabilityOptions(value);
        break;
      default:
        console.warn(`Unknown field: ${field}`);
    }
  };

  // Generate supplySignal value from signal and voltage selections
  const generateSupplySignalValue = (signal, voltage) => {
    const voltageFormatted = voltage === '3.3V' ? '3p3V' : '5V';
    if (signal === 'Sine') {
      return `sWave${voltageFormatted}`;
    } else if (signal === 'HCMOS') {
      return `hcmos${voltageFormatted}`;
    }
    return '';
  };

  // Get the display label for the selected combination
  const getSelectedSupplySignalLabel = () => {
    if (formData.signal && formData.supply) {
      return `${formData.signal} ${formData.supply}`;
    }
    return 'Not selected';
  };

  // Generate ordering code
  const getOrderingCode = () => {
    const voltage = formData.supply === '3.3V' ? '3' : '5';
    const signalType = formData.signal === 'Sine' ? 'S' : 'C';
    const freq = parseFloat(formData.frequency) < 10 
      ? `0${parseFloat(formData.frequency)}` 
      : parseFloat(formData.frequency);
    
    // Use product name from currentProduct if available
    const productName = currentProduct?.name || 'XBO14S';
    
    return `${productName}-${formData.tempRange}-${formData.stability}-${formData.aging}-${voltage}-${signalType}-${freq}`;
  };

  // Get min/max frequency from config
  const frequencyConfig = partNumberConfig?.frequencyInput || { type: "input", array: [8, 300] };
  const frequencyMin = frequencyConfig.array?.[0] || 8;
  const frequencyMax = frequencyConfig.array?.[1] || 300;

  // Check if both voltage and signal are selected
  const isSupplySignalComplete = formData.supply && formData.signal;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 bg-gray-50 p-6">
        <div className="flex items-center justify-center flex-col">
          <div className="text-center mb-1">
            <h3 className="text-xl md:text-3xl font-bold border-b-4 border-blue-300 inline-block pb-1">
              OCXO Configuration Form
            </h3>
          </div>
          
          <div className="text-center">
            {!isMobile ? (
              <p className="text-gray-600">
                Complete all fields to generate your ordering partnumber code
              </p>
            ) : (
              <p>Complete all fields</p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4 px-4 mt-8 pb-4">
        {/* Form Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Temperature Range */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Temperature Range *
              </label>
              <div className="grid grid-cols-3 gap-2">
                {temperatureRanges.map(range => (
                  <label key={range.code} className="relative">
                    <input
                      type="radio"
                      name="tempRange"
                      value={range.code}
                      checked={formData.tempRange === range.code}
                      onChange={(e) => handleInputChange('tempRange', e.target.value)}
                      className="sr-only peer"
                    />
                    <div className="p-3 text-center border rounded-lg cursor-pointer transition-all peer-checked:border-blue-500 peer-checked:bg-blue-50 hover:bg-gray-50">
                      <div className="font-medium">{range.range}</div>
                      <div className="text-xs text-gray-500 mt-1">Code {range.code}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Frequency */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Frequency (MHz) *
              </label>
              <input
                type="number"
                min={frequencyMin}
                max={frequencyMax}
                step="0.001"
                value={formData.frequency}
                onChange={(e) => handleInputChange('frequency', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter frequency"
              />
              <div className="text-xs text-gray-500 mt-2">
                Valid range: {frequencyMin} - {frequencyMax} MHz
              </div>
            </div>

            {/* Supply & Signal - Combined in Preview Style */}
            <div>

<div>
  <div className="p-4 rounded-lg border border-gray-200">
    
    {/* Two-column layout: Supply | Signal */}
    <div className="grid grid-cols-2 gap-4">
      {/* Supply Column */}
      <div className="space-y-2">
        <div className="text-xs font-medium text-gray-500 mb-1 flex items-center">
          <span className="w-2 h-2 rounded-full bg-amber-400 mr-1"></span>
          SUPPLY
        </div>
        <div className="grid grid-cols-1 gap-2">
          {supplyTypes.map((supply) => (
            <label key={supply.value} className="relative block">
              <input
                type="radio"
                name="voltage"
                value={supply.value}
                checked={formData.supply === supply.value}
                onChange={(e) => handleInputChange('voltage', e.target.value)}
                className="sr-only peer"
              />
              {/* Different background colors based on which voltage is selected */}
              <div className={`
                py-2 px-3 text-center border-2 rounded-lg cursor-pointer transition-all 
                hover:border-gray-300 hover:bg-gray-50
                border-gray-200
                ${formData.supply === '3.3V' && supply.value === '3.3V' 
                  ? ' peer-checked:bg-amber-50'                   
                  : formData.supply === '5V' && supply.value === '5V'
                  ? ' peer-checked:bg-amber-50'
                  : ' peer-checked:bg-gray-50'
                }
              `}>
                <div className="font-bold text-gray-800">{supply.voltage}</div>
                <div className="text-xs text-gray-500 mt-0.5">Supply</div>
              </div>
            </label>
          ))}
        </div>
      </div>
      
      {/* Signal Column */}
      <div className="space-y-2">
        <div className="text-xs font-medium text-gray-500 mb-1 flex items-center">
          <span className="w-2 h-2 rounded-full bg-purple-400 mr-1"></span>
          SIGNAL
        </div>
        <div className="grid grid-cols-1 gap-2">
          {signalTypes.map((signal) => (
            <label key={signal.value} className="relative block">
              <input
                type="radio"
                name="signal"
                value={signal.value}
                checked={formData.signal === signal.value}
                onChange={(e) => handleInputChange('signal', e.target.value)}
                className="sr-only peer"
              />
              {/* Different background colors based on which signal is selected */}
              <div className={`
                py-2 px-3 text-center border-2 rounded-lg cursor-pointer transition-all 
                hover:border-gray-300 hover:bg-gray-50
                border-gray-200
                ${formData.signal === 'Sine' && signal.value === 'Sine' 
                  ? 'peer-checked:bg-purple-50' 
                  : formData.signal === 'HCMOS' && signal.value === 'HCMOS'
                  ? 'peer-checked:bg-purple-50'
                  : 'peer-checked:bg-gray-50'

                }
              `}>
                <div className="font-bold text-gray-800">{signal.code}</div>
                <div className="text-xs text-gray-500 mt-0.5">Signal</div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  </div>
</div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Aging Factor */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Aging Factor (ppb/day) *
              </label>
              <div className="grid grid-cols-3 gap-2">
                {agingOptions.map(option => (
                  <label key={option.code} className="relative">
                    <input
                      type="radio"
                      name="aging"
                      value={option.code}
                      checked={formData.aging === option.code}
                      onChange={(e) => handleInputChange('aging', e.target.value)}
                      className="sr-only peer"
                    />
                    <div className="p-3 text-center border rounded-lg cursor-pointer transition-all peer-checked:border-amber-500 peer-checked:bg-amber-50 hover:bg-gray-50">
                      <div className="font-medium">{option.ppbPerDay}</div>
                      <div className="text-xs text-gray-500 mt-1">Code {option.code}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Stability */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Stability vs Temperature *
              </label>
              <select
                value={formData.stability}
                onChange={(e) => handleInputChange('stability', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {stabilityOptions.map(option => (
                  <option key={option.code} value={option.code}>
                    ±{option.value} {option.unit} (Code {option.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Configuration Preview */}
{/* Configuration Preview */}
<div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
  <div className="text-sm font-semibold text-gray-700 mb-3">Configuration Preview</div>
  <div className="space-y-2">
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">Temperature Range:</span>
      <span className="font-medium">
        {temperatureRanges.find(t => t.code === formData.tempRange)?.range || '—'}
      </span>
    </div>
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">Frequency:</span>
      <span className="font-medium">{formData.frequency || '—'} MHz</span>
    </div>
    
    {/* Separate Supply and Signal entries with colored values */}
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">Supply:</span>
      <span className="font-medium">
        {formData.supply || '—'}
      </span>
    </div>
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">Signal:</span>
      <span className="font-medium">
        {formData.signal || '—'}
      </span>
    </div>
    
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">Aging Factor:</span>
      <span className="font-medium">
        {agingOptions.find(a => a.code === formData.aging)?.ppbPerDay || '—'} ppb/day
      </span>
    </div>
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">Stability:</span>
      <span className="font-medium">
        ±{stabilityOptions.find(s => s.code === formData.stability)?.value || '—'} ppb
      </span>
    </div>
  </div>
</div>
          </div>
        </div>

        {/* Ordering Code & Actions */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
            <div className="text-center mb-6">
              <div className="text-sm text-gray-600 mb-4">Your Ordering Partnumber Code</div>
              <div className="font-mono text-lg md:text-5xl font-bold text-blue-600 break-all">
                {formData.partNumber}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional requirements
          </label>
          <textarea
            name="additionalRequirements"
            onChange={(e) => handleInputChange('additionalRequirements', e.target.value)}
            value={selectedFormAdditionalRequirements}
            rows="3"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder="Brief description of your application or project..."
          />
        </div>
      </div>
    </div>
  );
};

export default A1LayoutOcxoParams;